import type { DropActionAreaSpecifier, MemoryDragDropAction } from 'constants/memory-dnd';
import MemoryDnD, { MemoryDragDropArea } from 'constants/memory-dnd';
import * as hoursActions from 'redux/modules/hours.actions';
import * as hoursTypes from 'redux/modules/hours.types';
import { dismissSuggestionId, updateSuggestionEntries } from 'redux/modules/suggestions.actions';
import * as timelineActions from 'redux/modules/timeline.actions';
import { getHourEntities, getUnprocessedHours } from 'redux/selectors/hours';
import {
  canAutoUpdateDraftDuration,
  getDurationOfSelectedMemories,
} from 'redux/selectors/hours.draft';
import { getHourDraftEntryIds } from 'redux/selectors/hours.draft.base';
import { getSuggestionEntities, getSuggestions } from 'redux/selectors/suggestions';
import { getTimelineSelection } from 'redux/selectors/timeline';
import { getMemoryEntries } from 'redux/selectors/timeline.filters';
import { getHasEnabledTic } from 'redux/selectors/user';
import { TrackingActions } from 'tracking/actions';
import type { Hour, HourDraft } from 'types/hour';
import type { Thunk } from 'types/redux';
import type { Suggestion } from 'types/suggestion';
import { processDurationOnChangeToMemories } from 'utils/memories';

/** Only used by Classic */
export function toggleEntryIdsOnHourDraft(ids: Array<number>): Thunk {
  return (dispatch, getState) => {
    dispatch(
      updateDraftAndAdjustDuration(() => {
        dispatch(hoursTypes.toggleEntryIdsOnHourDraft({ entry_ids: ids }));
      })
    );

    const state = getState();
    const selectedIds = getHourDraftEntryIds(state);

    // Only track when at least one of the entry ids became selected
    if (ids.some(id => selectedIds.includes(id))) {
      TrackingActions.addedMemoryToTimeEntry(state);
    }
  };
}

export function toggleEntryIdsOnPendingDraft(entryIds: Array<number> = []): Thunk {
  return dispatch => {
    dispatch(timelineActions.toggleSelectEntryIds(entryIds));
  };
}

export function addEntryIdsOnHourDraft(ids: Array<number>, dragged = false): Thunk {
  return (dispatch, getState) => {
    dispatch(
      updateDraftAndAdjustDuration(() => {
        dispatch(hoursTypes.addEntryIdsOnHourDraft({ entry_ids: ids }));
      })
    );
    TrackingActions.addedMemoryToTimeEntry(getState(), dragged);
  };
}

export function removeEntryIdsOnHourDraft(ids: Array<number>): Thunk {
  return dispatch => {
    dispatch(
      updateDraftAndAdjustDuration(() => {
        dispatch(hoursTypes.removeEntryIdsOnHourDraft({ entry_ids: ids }));
      })
    );
  };
}

function updateDraftAndAdjustDuration(effectOnDraft: () => void): Thunk {
  return (dispatch, getState) => {
    const shouldUpdateDuration = canAutoUpdateDraftDuration(getState());
    effectOnDraft();

    // After the update, set new duration
    if (shouldUpdateDuration) {
      const newDuration = getDurationOfSelectedMemories(getState());
      dispatch(hoursActions.updateHourDraft({ duration: newDuration }));
    }
  };
}

export function handleDroppedMemory(props: MemoryDragDropAction): Thunk {
  return async (dispatch, getState) => {
    const { memoryIds, target, source } = props;

    if (source.area === MemoryDnD.AREA.HourDraft && target.area === MemoryDnD.AREA.MemoryInbox) {
      dispatch(removeEntryIdsOnHourDraft(memoryIds));
      return;
    }

    if (target.area === MemoryDnD.AREA.HourDraft) {
      dispatch(addEntryIdsOnHourDraft(memoryIds, true));
      return;
    }

    // First clean up
    await dispatch(disassociateMemories(props.memoryIds, { except: target }));

    if (target.area === MemoryDnD.AREA.MemoryInbox) {
      // Clean up is enough
      return;
    }

    if (target.area === MemoryDnD.AREA.PendingDraft) {
      dispatch(timelineActions.toggleSelectEntryIds(memoryIds, true));
      return;
    }

    const state = getState();

    // Suggestion target / source
    const suggestions = getSuggestions(state);
    if (target.area === MemoryDnD.AREA.AISuggestion) {
      const suggestion = suggestions.find(({ id }) => id === target.suggestionId);
      let entryIds: Array<number> | null = null;
      if (suggestion != null) {
        entryIds = suggestion.suggested_entry_ids;
      }
      if (entryIds != null) {
        const combinedEntryIds = entryIds.concat(memoryIds);
        dispatch(updateSuggestionEntries(target.suggestionId, combinedEntryIds));
        TrackingActions.addedMemoryToTimeEntry(state, true);
      }
    }

    // Logged hours
    const entries = getMemoryEntries(state);
    const loggedHourEntities = getHourEntities(state);
    if (target.area === MemoryDnD.AREA.LoggedHour) {
      const targetHour = loggedHourEntities[target.hourId];
      if (targetHour != null) {
        const combinedEntryIds = targetHour.entry_ids.concat(memoryIds);
        const { newDuration } = processDurationOnChangeToMemories({
          entries,
          idsBefore: targetHour.entry_ids,
          idsAfter: combinedEntryIds,
          duration: targetHour.duration,
        });
        dispatch(
          hoursActions.saveHour(
            {
              ...targetHour,
              duration: newDuration,
              entry_ids: combinedEntryIds,
              context: { interaction: 'Drag and Drop Memory' },
            },
            { restoreIgnoredMemories: true, hasChangedEntryIds: true }
          )
        );
        TrackingActions.addedMemoryToTimeEntry(state, true);
      }
    }
  };
}

const disassociateMemoriesFromLoggedHour =
  (loggedHour: Hour<number>, memoryIds: Array<number>): Thunk<Promise<unknown>> =>
  (dispatch, getState) => {
    const filteredEntryIds = loggedHour.entry_ids.filter(id => !memoryIds.includes(id));
    if (filteredEntryIds.length === loggedHour.entry_ids.length) return Promise.resolve();

    const entries = getMemoryEntries(getState());
    const { newDuration } = processDurationOnChangeToMemories({
      entries,
      idsBefore: loggedHour.entry_ids,
      idsAfter: filteredEntryIds,
      duration: loggedHour.duration,
    });

    return dispatch(
      hoursActions.saveHour(
        {
          ...loggedHour,
          entry_ids: filteredEntryIds,
          duration: newDuration,
          context: { interaction: 'Drag and Drop Memory' },
        },
        { hasChangedEntryIds: true }
      )
    );
  };

const disassociateMemoriesFromSuggestion =
  (suggestion: Suggestion<number>, memoryIds: Array<number>): Thunk<Promise<unknown>> =>
  dispatch => {
    const filteredEntryIds = suggestion.suggested_entry_ids.filter(id => !memoryIds.includes(id));
    if (filteredEntryIds.length === suggestion.suggested_entry_ids.length) return Promise.resolve();

    if (filteredEntryIds.length === 0) {
      // Clean up suggestions that become empty
      return dispatch(dismissSuggestionId(suggestion.id));
    } else {
      return dispatch(updateSuggestionEntries(suggestion.id, filteredEntryIds));
    }
  };

export const disassociateMemoriesFromSelection =
  (memoryIds: Array<number>): Thunk =>
  (dispatch, getState) => {
    const state = getState();
    const selection = getTimelineSelection(state);
    const selectedIds = memoryIds.filter(id => selection.entryIds.includes(id));
    dispatch(timelineActions.toggleSelectEntryIds(selectedIds));
  };

export function disassociateMemories(
  memoryIds: Array<number>,
  options: { except?: DropActionAreaSpecifier } = {}
): Thunk<Promise<unknown>> {
  return (dispatch, getState) => {
    const state = getState();
    const promises: Array<Promise<unknown>> = [];
    // Clean up any suggestions that has any of these memory ids
    // We cannot trust source.area to work, since the user can drag "logged" items from timeline
    const suggestions = getSuggestions(state);
    suggestions
      .filter(
        // Avoid removing from current
        s =>
          !(
            options.except?.area === MemoryDragDropArea.AISuggestion &&
            options.except.suggestionId === s.id
          )
      )
      .forEach(s => dispatch(disassociateMemoriesFromSuggestion(s, memoryIds)));

    // Clean up any hours that has any of these memory ids
    // We cannot trust source.area to work, since the user can drag "logged" items from timeline
    const loggedHours = getUnprocessedHours(state);
    loggedHours
      .filter(
        // Avoid removing from current
        s =>
          !(
            options.except?.area === MemoryDragDropArea.LoggedHour && options.except.hourId === s.id
          )
      )
      .forEach(h => dispatch(disassociateMemoriesFromLoggedHour(h, memoryIds)));

    // Clean up from selection
    dispatch(disassociateMemoriesFromSelection(memoryIds));

    return Promise.all(promises);
  };
}

export const disassociateMemoriesFromLoggedHourOrSuggestion =
  (memoryIds: Array<number>, specifier: { target: 'suggestion' | 'hour'; id: number }): Thunk =>
  (dispatch, getState) => {
    const state = getState();

    if (specifier.target === 'suggestion') {
      const suggestion = getSuggestionEntities(state)[specifier.id];
      if (suggestion) {
        dispatch(disassociateMemoriesFromSuggestion(suggestion, memoryIds));
        return;
      }
    }

    if (specifier.target === 'hour') {
      const loggedHour = getHourEntities(state)[specifier.id];
      if (loggedHour) {
        dispatch(disassociateMemoriesFromLoggedHour(loggedHour, memoryIds));
      }
    }
  };

/**
 * Currently, backend does not remove memories from AI drafts (suggestions) when they are logged
 * to an hour entry.
 * Usages of this function should be replaced with a
 * call to saveHourDraft when backend does this for us
 * */
export const saveHourDraftAndDisassociateMemoriesFromElsewhere =
  (partialDraft: Partial<HourDraft>, options?: { clearDraft: boolean }): Thunk<Promise<unknown>> =>
  async (dispatch, getState) => {
    const isTicEnabled = getHasEnabledTic(getState());

    const hour = await dispatch(hoursActions.saveHourDraft(partialDraft, options));

    // Timely classic wants to adjust suggestions when a draft is saved
    if (!isTicEnabled) {
      // Need to getState() again since we dispatched an action that affects suggestions
      getSuggestions(getState()).forEach(s =>
        dispatch(disassociateMemoriesFromSuggestion(s, hour.entry_ids))
      );
    }
  };
